import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from '@/auth/useAuth';
import { isLocalMode } from '@/services/localRuntimeClient';
import {
  getApplicationTransitionKey,
  useApplicationTransitionStore,
  type ApplicationTransitionIntent,
  type ApplicationTransitionTarget,
} from '@/stores/applicationTransitionStore';
import {
  getApplication,
  startApplication,
  stopApplication,
  getScaleConfiguration,
  updateScaleConfiguration,
  listRevisions,
  getImageTags,
  setImage,
} from '@/services/applicationManagementApi';
import type {
  AutomationApp,
  Revision,
  RunningStatus,
  ScaleConfiguration,
  SetImageRequest,
  StartStopResponse,
} from '@/types/automation';

// =============================================================================
// HELPERS
// =============================================================================

/** Token acquisition helper — skips the async fetch in local mode. */
const useTokenFetcher = () => {
  const { getOttoToken } = useAuth();
  return async (): Promise<string> => {
    if (isLocalMode()) return '';
    const token = await getOttoToken();
    if (!token) throw new Error('Failed to acquire Otto token');
    return token;
  };
};

export const APPLICATION_STATUS_POLL_INTERVAL_MS = 5_000;
export const APPLICATION_TRANSITION_TIMEOUT_MS = 5 * 60 * 1_000;

type TransitionalRunningStatus = Extract<RunningStatus, 'Starting' | 'Stopping'>;

/** Returns true when the status is transitional and should trigger frequent polling. */
const isTransitionalStatus = (status?: string | null): status is TransitionalRunningStatus =>
  status === 'Starting' || status === 'Stopping';

const getTransitionalStatus = (target: ApplicationTransitionTarget): TransitionalRunningStatus =>
  target === 'Running' ? 'Starting' : 'Stopping';

export const getApplicationStatusRefetchInterval = (
  status: RunningStatus | null | undefined,
  transition: ApplicationTransitionIntent | undefined,
  now: number = Date.now(),
): number | false => {
  if (isTransitionalStatus(status)) {
    return APPLICATION_STATUS_POLL_INTERVAL_MS;
  }

  if (transition && transition.expiresAt > now && status !== transition.target) {
    return APPLICATION_STATUS_POLL_INTERVAL_MS;
  }

  return false;
};

/** The most recently created revision, or undefined if the list is empty. */
export const getLatestRevision = (revisions: readonly Revision[] | undefined): Revision | undefined =>
  revisions?.reduce<Revision | undefined>((latest, r) =>
    !latest || new Date(r.createdTime).getTime() > new Date(latest.createdTime).getTime() ? r : latest,
  undefined);

/**
 * Whether a revision is receiving at least some traffic. `trafficWeight` is
 * authoritative: ACA can mark a new revision `active` before traffic has
 * switched, so an explicit zero traffic weight must keep the gate closed.
 *
 * Otto runs ACA in single-revision mode, where the newest healthy revision is
 * expected to receive 100% and older revisions drop to 0%. Do not require
 * `trafficWeight === 100` or exclusive traffic, though: if a future app ever
 * has a durable split, that would deadlock Test Draft forever instead of
 * failing open for the fraction still routed to an old container.
 */
const isServing = (revision: Revision): boolean => revision.trafficWeight > 0;

/**
 * True while the app's newest revision is not yet the one answering requests.
 *
 * Writing a connection secret or an app setting stores it as a container
 * environment variable, which ACA can only apply by rolling a new revision and
 * shifting traffic to it. Until that cutover completes the previous container
 * still serves, and it does not have the new setting — that is what made a run
 * started right after saving a connection fail to authenticate.
 *
 * Only active rollout states keep the gate closed:
 * 1. `provisioningState === 'Provisioning'` — the revision is still building.
 * 2. `provisioningState === 'Provisioned'` but not serving traffic — this
 *    is the window in which the old container would still answer the run.
 *
 * Terminal, teardown, and unrecognized states deliberately fail open. A failed
 * or deprovisioning revision will never begin serving, so blocking here would
 * deadlock Test Draft and poll `/revisions` forever; the run itself should
 * surface the real backend failure.
 *
 * Ready replicas are deliberately NOT required. A serving revision with zero
 * ready replicas is a scaled-to-zero app, and only an incoming request can wake
 * it, so waiting for replicas would block the very request that starts the
 * container. This also means polling stops for a sleeping app instead of
 * running forever.
 *
 * No revisions at all (empty list, or a failed request) reports "not in flight"
 * so the run can proceed and surface any real backend failure itself.
 */
export const isRevisionRolloutInFlight = (revisions: readonly Revision[] | undefined): boolean => {
  const latest = getLatestRevision(revisions);
  if (!latest) return false;
  if (latest.provisioningState === 'Provisioning') return true;
  if (latest.provisioningState === 'Provisioned') return !isServing(latest);
  return false;
};

/**
 * Whether the Test Draft gate must stay closed.
 *
 * Evaluated from the revisions query the dialog itself runs, so opening the
 * dialog is what checks readiness. That covers the reported bug directly: a
 * connection is created in the node editor, and by the time the dialog is
 * opened this query reports the rollout that the write triggered.
 *
 * Takes both React Query's `isPending` and `isFetching`. `isPending` covers the
 * pre-auth pending-idle window, when the query is not enabled yet and therefore
 * is not fetching. `isFetching` covers the initial fetch and stale-data
 * refetches. The latter matters because a save invalidates the revisions query,
 * and during that refetch React Query keeps serving the previous data. That
 * stale snapshot still describes the old revision as serving, so gating on
 * pending state alone would leave Test Draft enabled while a new revision is
 * spinning up.
 *
 * Spurious blocking is not a concern: QueryProvider disables refetch on mount,
 * window focus, and reconnect, so `isFetching` only rises on the first fetch,
 * an explicit invalidation, or the 3s poll that runs while a rollout is in
 * flight.
 *
 * A query *error* intentionally opens the gate rather than closing it: the
 * error leaves `data` undefined with pending and fetching both false, and
 * blocking runs indefinitely on a revisions API outage is worse than letting
 * the run itself surface the failure.
 */
export const isTestDraftGateBlocked = (
  revisions: readonly Revision[] | undefined,
  isRevisionsFetching: boolean,
  isRevisionsPending: boolean,
): boolean => {
  if (isRevisionsFetching || isRevisionsPending) return true;
  return isRevisionRolloutInFlight(revisions);
};

// =============================================================================
// QUERY KEYS
// =============================================================================

export const applicationKeys = {
  status: (project: string, app: string) => ['applicationStatus', project.toLowerCase(), app.toLowerCase()] as const,
  scale: (project: string, app: string) => ['scaleConfiguration', project.toLowerCase(), app.toLowerCase()] as const,
  revisions: (project: string, app: string) => ['revisions', project.toLowerCase(), app.toLowerCase()] as const,
  imageTags: (project: string, app: string) => ['imageTags', project.toLowerCase(), app.toLowerCase()] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetches the application including its live runningStatus.
 * Polls every 5s when the status is transitional (Starting/Stopping),
 * otherwise uses standard staleTime.
 */
export const useApplicationStatus = (projectName: string, appName: string) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();
  const transitionKey = getApplicationTransitionKey(projectName, appName);
  const transition = useApplicationTransitionStore((state) => state.transitions[transitionKey]);
  const clearTransition = useApplicationTransitionStore((state) => state.clearTransition);

  const query = useQuery({
    queryKey: applicationKeys.status(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      return getApplication(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const status = query.state.data?.properties?.runningStatus;
      return getApplicationStatusRefetchInterval(status, transition);
    },
    select: (application) => {
      const runningStatus = application.properties.runningStatus;
      if (
        !transition ||
        transition.expiresAt <= Date.now() ||
        runningStatus === transition.target ||
        isTransitionalStatus(runningStatus)
      ) {
        return application;
      }

      return {
        ...application,
        properties: {
          ...application.properties,
          runningStatus: getTransitionalStatus(transition.target),
        },
      };
    },
  });

  const runningStatus = query.data?.properties?.runningStatus;
  useEffect(() => {
    if (!transition) {
      return;
    }

    if (runningStatus === transition.target || Date.now() >= transition.expiresAt) {
      clearTransition(transitionKey, transition);
      return;
    }

    const timeoutId = window.setTimeout(
      () => clearTransition(transitionKey, transition),
      transition.expiresAt - Date.now(),
    );
    return () => window.clearTimeout(timeoutId);
  }, [clearTransition, runningStatus, transition, transitionKey]);

  return query;
};

/**
 * Fetches the scale configuration for an application.
 */
export const useScaleConfiguration = (projectName: string, appName: string) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();

  return useQuery({
    queryKey: applicationKeys.scale(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      return getScaleConfiguration(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetches the revision list for an application.
 *
 * Polls every 3s while a revision rollout is in flight, so the Test Draft gate
 * and the Revisions tab reflect the cutover without a manual refresh. Polling
 * stops as soon as the newest revision is the one serving traffic, which also
 * means a scaled-to-zero app is not polled indefinitely.
 */
export const useRevisions = (
  projectName: string,
  appName: string,
  options?: { enabled?: boolean },
) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();
  const callerEnabled = options?.enabled ?? true;
  const queryEnabled = callerEnabled && (isAuthenticated || isLocalMode()) && !!projectName && !!appName;

  const query = useQuery({
    queryKey: applicationKeys.revisions(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      const response = await listRevisions(projectName, appName, token);
      return response.value ?? [];
    },
    enabled: queryEnabled,
    staleTime: 30 * 1000,
    refetchInterval: (query) => (isRevisionRolloutInFlight(query.state.data) ? 3_000 : false),
  });

  const wasEnabled = useRef(queryEnabled);
  const { refetch } = query;
  useEffect(() => {
    const wasReEnabled = queryEnabled && !wasEnabled.current;
    wasEnabled.current = queryEnabled;

    if (wasReEnabled) {
      // QueryProvider disables automatic refetch-on-mount, and toggling
      // `enabled` on an existing observer does not apply refetchOnMount anyway.
      // A connection can roll a revision while Test Draft is closed, so force a
      // fresh readiness check whenever this query transitions back to enabled.
      void refetch();
    }
  }, [queryEnabled, refetch]);

  return query;
};

/**
 * Fetches available image tags for the application.
 * Tags change infrequently, so use a longer staleTime.
 */
export const useImageTags = (projectName: string, appName: string) => {
  const { isAuthenticated } = useAuth();
  const fetchToken = useTokenFetcher();

  return useQuery({
    queryKey: applicationKeys.imageTags(projectName, appName),
    queryFn: async () => {
      const token = await fetchToken();
      return getImageTags(projectName, appName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 5 * 60 * 1000,
  });
};

// =============================================================================
// MUTATIONS
// =============================================================================

type ApplicationAction = (
  projectName: string,
  appName: string,
  token: string,
) => Promise<StartStopResponse>;

const useApplicationAction = (
  projectName: string,
  appName: string,
  action: ApplicationAction,
  target: ApplicationTransitionTarget,
  transitionalStatus: TransitionalRunningStatus,
) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();
  const setTransition = useApplicationTransitionStore((state) => state.setTransition);

  return useMutation({
    mutationFn: async () => {
      const token = await fetchToken();
      return action(projectName, appName, token);
    },
    onSuccess: (response) => {
      setTransition(getApplicationTransitionKey(projectName, appName), {
        target,
        expiresAt: Date.now() + APPLICATION_TRANSITION_TIMEOUT_MS,
      });

      if (response.status === transitionalStatus) {
        queryClient.setQueryData<AutomationApp>(
          applicationKeys.status(projectName, appName),
          (application) =>
            application
              ? {
                  ...application,
                  properties: {
                    ...application.properties,
                    runningStatus: transitionalStatus,
                  },
                }
              : application,
        );
      }

      queryClient.invalidateQueries({ queryKey: applicationKeys.status(projectName, appName) });
    },
  });
};

/**
 * Starts an application and keeps polling until Running is observed or the
 * transition deadline expires.
 */
export const useStartApplication = (projectName: string, appName: string) =>
  useApplicationAction(projectName, appName, startApplication, 'Running', 'Starting');

/**
 * Stops an application and keeps polling until Stopped is observed or the
 * transition deadline expires.
 */
export const useStopApplication = (projectName: string, appName: string) =>
  useApplicationAction(projectName, appName, stopApplication, 'Stopped', 'Stopping');

/**
 * Updates the scale configuration. Caches the response directly
 * (like env-settings) since a GET immediately after may return stale data.
 */
export const useUpdateScaleConfiguration = (projectName: string, appName: string) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: ScaleConfiguration) => {
      const token = await fetchToken();
      return updateScaleConfiguration(projectName, appName, token, config);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(applicationKeys.scale(projectName, appName), data);
    },
  });
};

/**
 * Sets the container image (changes runtime version).
 * Invalidates revisions + application status since the change
 * creates a new revision and may briefly affect runningStatus.
 */
export const useSetImage = (projectName: string, appName: string) => {
  const fetchToken = useTokenFetcher();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SetImageRequest) => {
      const token = await fetchToken();
      return setImage(projectName, appName, token, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.revisions(projectName, appName) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.status(projectName, appName) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.imageTags(projectName, appName) });
    },
  });
};
